// src/components/custom/ConcussionBlock.jsx
//
// The designed Concussions page (data/concussions.js), carried ABOVE a private
// topic's modules when its doc says `block: 'concussions'`, the way PledgeBlock
// carries the Player Pledge. Three audiences on one page, picked by who is
// looking (a player, a parent, a coach) and switchable; the symptom groups, the
// red flags, the two six-step ladders, the spike rule and the paperwork as
// designed cards rather than pasted text. Sport words come from the org's
// vocabulary. The page tracks nothing: there is no input on it.

import React, { useState } from 'react';
import {
    Activity, Brain, Smile, Moon, Siren, Stethoscope, GraduationCap, ClipboardCheck,
    ShieldCheck, ExternalLink, AlertTriangle, CheckCircle2, Users
} from 'lucide-react';
import { CONCUSSIONS, CONCUSSION_SOURCES } from '../../data/concussions';
import { resolveSport, fillSportDeep } from '../../data/sports';

const GROUP_ICONS = { body: Activity, brain: Brain, mood: Smile, sleep: Moon };

const defaultAudience = (user) => {
    if (!user) return 'players';
    if (user.role === 'coach' || user.role === 'admin' || user.role === 'super_admin') return 'coaches';
    if (user.memberType === 'spouse' || user.memberType === 'parent') return 'parents';
    return 'players';
};

const Card = ({ children, className = '' }) => (
    <div className={`bg-white rounded-2xl border border-slate-200 p-5 ${className}`}>{children}</div>
);

const Kicker = ({ icon: Icon, children }) => (
    <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
        {Icon && <Icon size={14} className="text-brand-primary" />}{children}
    </p>
);

const NumberedList = ({ items }) => (
    <ol className="space-y-2">
        {items.map((t, i) => (
            <li key={i} className="flex gap-3 items-start">
                <span className="w-7 h-7 rounded-xl bg-brand-light text-brand-primary flex items-center justify-center font-black shrink-0 text-xs mt-0.5">{i + 1}</span>
                <span className="text-sm text-slate-700 font-medium leading-relaxed">{t}</span>
            </li>
        ))}
    </ol>
);

const Ladder = ({ ladder, icon: Icon }) => (
    <Card>
        <Kicker icon={Icon}>{ladder.title}</Kicker>
        <p className="text-sm text-slate-600 font-medium leading-relaxed mb-4">{ladder.subtitle}</p>
        <ol className="relative border-l-2 border-slate-200 ml-3 space-y-4">
            {ladder.steps.map((s, i) => (
                <li key={i} className="pl-6 relative">
                    <span className={`absolute -left-[15px] top-0 w-7 h-7 rounded-full flex items-center justify-center font-black text-xs border-2 border-white ${s.clearance ? 'bg-amber-400 text-slate-900' : 'bg-brand-primary text-white'}`}>{i + 1}</span>
                    <p className="font-bold text-slate-800 text-sm leading-tight flex flex-wrap items-center gap-2">
                        {s.title}
                        {s.clearance && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-black uppercase tracking-wider">Medical clearance first</span>}
                    </p>
                    {s.text && <p className="text-sm text-slate-500 leading-relaxed mt-0.5">{s.text}</p>}
                </li>
            ))}
        </ol>
        {ladder.footnote && <p className="text-xs text-slate-500 font-medium leading-relaxed mt-4 pt-3 border-t border-slate-100">{ladder.footnote}</p>}
    </Card>
);

const Symptoms = ({ symptoms }) => (
    <Card>
        <Kicker icon={Stethoscope}>Know the symptoms</Kicker>
        <p className="text-sm text-slate-600 font-medium leading-relaxed mb-4">{symptoms.intro}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {symptoms.groups.map((g) => {
                const Icon = GROUP_ICONS[g.icon] || Activity;
                return (
                    <div key={g.id} className="rounded-2xl p-4 border border-slate-100" style={{ backgroundColor: 'var(--brand-highlight)' }}>
                        <p className="text-sm font-black text-slate-900 flex items-center gap-2 mb-2"><Icon size={16} className="text-brand-primary" />{g.title}</p>
                        <ul className="space-y-1">
                            {g.items.map((it, i) => <li key={i} className="text-sm text-slate-600 font-medium leading-snug flex gap-2"><span className="text-brand-primary">•</span>{it}</li>)}
                        </ul>
                    </div>
                );
            })}
        </div>
    </Card>
);

const Emergency = ({ emergency }) => (
    <div className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-5">
        <p className="text-[11px] font-black text-rose-700 uppercase tracking-widest mb-2 flex items-center gap-2"><Siren size={14} />{emergency.title}</p>
        <p className="text-sm font-bold text-rose-900 mb-3">{emergency.lead}</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
            {emergency.items.map((it, i) => <li key={i} className="text-sm text-rose-900 font-medium leading-snug flex gap-2"><AlertTriangle size={14} className="text-rose-500 shrink-0 mt-0.5" />{it}</li>)}
        </ul>
    </div>
);

const Spike = ({ spike }) => (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
        <Kicker icon={Activity}>{spike.title}</Kicker>
        <p className="text-sm text-slate-700 font-medium leading-relaxed">{spike.text}</p>
    </div>
);

const Paperwork = ({ paperwork, onLink }) => (
    <Card>
        <Kicker icon={ClipboardCheck}>{paperwork.title}</Kicker>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {paperwork.cards.map((c, i) => (
                <div key={i} className="rounded-2xl border border-slate-100 p-4 bg-slate-50 flex flex-col">
                    <p className="text-sm font-black text-slate-900">{c.title}</p>
                    <p className="text-sm text-slate-600 font-medium leading-relaxed mt-1 flex-1">{c.text}</p>
                    {c.link && (
                        <a href={c.link} target="_blank" rel="noopener noreferrer" onClick={() => onLink && onLink(c.linkLabel)} className="inline-flex items-center gap-1.5 mt-3 text-xs font-black text-brand-primary hover:underline underline-offset-2">
                            <ExternalLink size={13} /> {c.linkLabel}
                        </a>
                    )}
                </div>
            ))}
        </div>
    </Card>
);

const ConcussionBlock = ({ currentUser, orgData, onAction = null }) => {
    const sport = resolveSport(orgData);
    const data = fillSportDeep(CONCUSSIONS, sport);
    const [audienceId, setAudienceId] = useState(() => defaultAudience(currentUser));
    const audience = data.audiences.find((a) => a.id === audienceId) || data.audiences[0];
    const show = new Set(audience.show || []);
    const link = (label) => onAction && onAction({ label });

    return (
        <div className="space-y-4">
            {/* Hero */}
            <div className="rounded-3xl border border-slate-200 p-6 md:p-8" style={{ backgroundColor: 'var(--brand-highlight)', borderLeft: '6px solid var(--brand-accent)' }}>
                <p className="text-[11px] font-black uppercase tracking-widest text-brand-primary mb-1 flex items-center gap-2"><ShieldCheck size={14} />{data.eyebrow}</p>
                <h2 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">{data.headline}</h2>
                <p className="text-sm font-bold text-slate-500 mt-1">{data.tagline}</p>
                <p className="text-sm text-slate-700 font-medium leading-relaxed mt-4 flex items-start gap-2"><CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />{data.promise}</p>

                {/* Audience switch */}
                <div className="flex flex-wrap gap-2 mt-5" role="tablist" aria-label="Who is reading">
                    {data.audiences.map((a) => (
                        <button
                            key={a.id} type="button" role="tab" aria-selected={a.id === audience.id}
                            onClick={() => setAudienceId(a.id)}
                            className={`px-4 py-2 rounded-full font-black text-sm flex items-center gap-2 transition-colors ${a.id === audience.id ? 'bg-brand-primary text-white shadow-lg shadow-brand-primary/25' : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'}`}
                        >
                            {a.id === 'coaches' && <Users size={14} />}{a.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* The audience's own opening: a lead and numbered steps, or the quoted obligations. */}
            <Card>
                <Kicker icon={audience.id === 'coaches' ? ShieldCheck : AlertTriangle}>{audience.lead}</Kicker>
                {Array.isArray(audience.quotes) && audience.quotes.length > 0 && (
                    <div className="space-y-2 mb-4">
                        {audience.quotes.map((q, i) => (
                            <blockquote key={i} className="rounded-2xl p-4 border-l-4 border-brand-primary bg-slate-50">
                                <p className="text-sm text-slate-800 font-medium leading-relaxed">“{q.text}”</p>
                                <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mt-2">{q.who}</p>
                            </blockquote>
                        ))}
                    </div>
                )}
                <NumberedList items={audience.steps} />
            </Card>

            {show.has('symptoms') && <Symptoms symptoms={data.symptoms} />}
            {show.has('emergency') && <Emergency emergency={data.emergency} />}

            {audience.next && (
                <Card>
                    <Kicker icon={Stethoscope}>{audience.next.title}</Kicker>
                    <NumberedList items={audience.next.items} />
                    {audience.next.outro && <p className="text-sm text-slate-500 font-medium leading-relaxed mt-4 pt-3 border-t border-slate-100">{audience.next.outro}</p>}
                </Card>
            )}

            {show.has('spike') && <Spike spike={data.spike} />}
            {(show.has('returnToPlay') || show.has('returnToLearn')) && (
                <div className={`grid grid-cols-1 gap-4 ${show.has('returnToPlay') && show.has('returnToLearn') ? 'lg:grid-cols-2' : ''}`}>
                    {show.has('returnToPlay') && <Ladder ladder={data.returnToPlay} icon={Activity} />}
                    {show.has('returnToLearn') && <Ladder ladder={data.returnToLearn} icon={GraduationCap} />}
                </div>
            )}
            {show.has('paperwork') && <Paperwork paperwork={data.paperwork} onLink={link} />}

            {/* Where it comes from. */}
            <div className="flex flex-wrap gap-2 items-center">
                <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest mr-1">Sources</span>
                {CONCUSSION_SOURCES.map((s, i) => (
                    <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" onClick={() => link(s.label)} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-bold text-slate-600 hover:border-brand-primary/40 hover:text-brand-primary flex items-center gap-1.5">
                        <ExternalLink size={12} /> {s.label}
                    </a>
                ))}
            </div>
        </div>
    );
};

export default ConcussionBlock;
